import { MoreVertical, Download, Edit, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";

interface MediaCardProps {
  media: {
    id: string;
    filename: string;
    storage_path: string;
    file_size: number;
    mime_type: string;
    width: number | null;
    height: number | null;
    created_at: string;
    alt_text: string | null;
    title?: string | null;
  };
  onEdit: (media: any) => void;
  onDelete: (id: string) => void;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (media: any) => void;
  onPreview?: (media: any) => void;
  tags?: string[];
}

export const MediaCard = ({ 
  media, 
  onEdit, 
  onDelete,
  selectable = false,
  selected = false,
  onSelect,
  onPreview,
  tags = []
}: MediaCardProps) => {
  const imageUrl = supabase.storage
    .from('media-library')
    .getPublicUrl(media.storage_path).data.publicUrl;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(imageUrl);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (selectable && onSelect) {
      onSelect(media);
    } else if (onPreview && !(e.target as HTMLElement).closest('button')) {
      onPreview(media);
    }
  };

  return (
    <div 
      className={`relative group bg-card border rounded-lg overflow-hidden transition-all ${
        selectable ? 'cursor-pointer hover:border-primary' : ''
      } ${selected ? 'border-primary ring-2 ring-primary' : ''}`}
      onClick={handleCardClick}
    >
      {selected && (
        <div className="absolute top-2 right-2 z-10 bg-primary text-primary-foreground rounded-full p-1">
          <Check className="h-4 w-4" />
        </div>
      )}
      
      <div className="aspect-square relative bg-muted cursor-pointer">
        <img
          src={imageUrl}
          alt={media.alt_text || media.filename}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="p-3">
        <p className="text-sm font-medium truncate" title={media.title || media.filename}>
          {media.title || media.filename}
        </p>
        
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {tags.slice(0, 2).map(tag => (
              <span key={tag} className="text-xs bg-secondary px-2 py-0.5 rounded">
                {tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="text-xs text-muted-foreground">+{tags.length - 2}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-muted-foreground">
            {formatFileSize(media.file_size)}
          </span>
          {media.width && media.height && (
            <span className="text-xs text-muted-foreground">
              {media.width} × {media.height}
            </span>
          )}
        </div>

        {!selectable && (
          <div className="flex items-center gap-1 mt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUrl();
              }}
            >
              <Download className="h-3 w-3 mr-1" />
              Copy URL
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="sm">
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  onEdit(media);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(media.id);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </div>
  );
};
